import { ExpandedRowHeader } from '../../../components/expandedRow/header/expandedRowHeader.component';
import { FC, useCallback, useState } from 'react';
import { IStaffDto, IStaffStudyDto } from '../../../services/api/api-client';
import { Trans, useTranslation } from 'react-i18next';
import { ReactComponent as EditIcon } from '../../../assets/img/common/edit_20.svg';
import { useModal } from '../../../application/hooks/useModal';
import Style from './userlist.module.scss';
import { EditUserModalContent } from './EditUserModalContent';
import { Chips } from 'uikit/chips/chips.component';
import { ReactComponent as DeleteIcon } from 'assets/img/common/delete_20.svg';
import { showServerErrorToast } from '../../../components/toast/toast-helper';
import { useQueryClient } from '@tanstack/react-query';
import ExpandedRowSections from 'uikit/table/ExpandedRowSections';
import PropertiesList from 'uikit/propertiesList/PropertiesList';
import { BulletPoints } from 'uikit/BulletPoints';
import { deleteUser, unassignStudy } from 'src/services/api/api-client/UsersClient';
import { getAllUsersQueryKey } from 'src/services/api/api-client/UsersQuery';
import { ConfirmResult, useConfirmationModal } from 'src/components/dialogModal/useConfirmationModal';
import { AppButtonWithTooltip } from 'uikit/buttons/buttonWithTooltip/buttonWithTooltip';
import { useEditUserStudyModal } from './useEditUserStudy.component';
import { LocalizedResourceDictionaryKeys } from 'src/application/localisation/i18next';
import { Collapse } from 'uikit/collapse/Collapse';
import { AppButton } from 'uikit/buttons';
import { ReactComponent as Chevron } from 'src/assets/img/common/chevron.svg';
import clsx from 'clsx';
import { conditionalConcat } from 'src/helpers/arrayHelpers';
import { useStaffProfile } from 'src/helpers/hooks/useStudy';
import { getDoctorStudiesQueryKey } from 'src/services/api/api-client/StudyQuery';
import { getDoctorProfileInfoQueryKey } from 'src/services/api/api-client/ProfileQuery';

export const UserDetailsCard = (props: IStaffDto) => {
  const { t } = useTranslation('dev');
  const client = useQueryClient();
  const { profile } = useStaffProfile();

  const editModalManager = useModal();
  const editUserStudyModal = useEditUserStudyModal();

  const unassignStudyConfirmationModal = useConfirmationModal();
  const removeUserConfirmationModal = useConfirmationModal();

  const onDelete = useCallback(async () => {
    try {
      const result = await removeUserConfirmationModal.open({
        title: t('Admin.UserList.Details.RemoveUserConfirmationModal.Title'),
        text: (
          <span>
            <Trans
              t={t}
              i18nKey={'Admin.UserList.Details.RemoveUserConfirmationModal.MainText'}
              values={{ name: props.fullName }}
              components={{
                Bold: <span className={Style.mediumText} />,
                Red: <span className={Style.awfulText} />,
              }}
            />
          </span>
        ),
        cancelButtonText: t('Admin.UserList.Details.RemoveUserConfirmationModal.CancelButtonText'),
        okButtonColorSchema: 'destroy',
        okButtonText: t('Admin.UserList.Details.RemoveUserConfirmationModal.OkButtonText'),
      });

      if (result !== ConfirmResult.Confirm) return;

      await deleteUser(props.id);
      await client.invalidateQueries(getAllUsersQueryKey());
    } catch (e) {
      showServerErrorToast(e);
    }
  }, [removeUserConfirmationModal, t, props.fullName, props.id, client]);

  const onUnassignStudy = useCallback(
    async (study: IStaffStudyDto) => {
      try {
        const result = await unassignStudyConfirmationModal.open({
          title: t('Admin.UserList.Details.UnassignStudyConfirmationModal.Title'),
          text: (
            <span>
              <Trans
                t={t}
                i18nKey={'Admin.UserList.Details.UnassignStudyConfirmationModal.MainText'}
                values={{ name: props.fullName, studyNumber: study.studyNumber, studyTitle: study.studyTitle }}
                components={{
                  Bold: <span className={Style.mediumText} />,
                  Red: <span className={Style.awfulText} />,
                }}
              />
            </span>
          ),
          cancelButtonText: t('Admin.UserList.Details.UnassignStudyConfirmationModal.CancelButtonText'),
          okButtonColorSchema: 'destroy',
          okButtonText: t('Admin.UserList.Details.UnassignStudyConfirmationModal.OkButtonText'),
        });

        if (result !== ConfirmResult.Confirm) return;

        await unassignStudy(props.id, study.id);
        await Promise.all(
          conditionalConcat(
            [client.invalidateQueries(getAllUsersQueryKey())],
            props.id === profile.id && [
              client.invalidateQueries(getDoctorProfileInfoQueryKey()),
              client.invalidateQueries(getDoctorStudiesQueryKey()),
            ],
          ),
        );
      } catch (e) {
        showServerErrorToast(e);
      }
    },
    [client, profile.id, props.fullName, props.id, t, unassignStudyConfirmationModal],
  );

  const onUserStudyEdit = useCallback(
    async (study: IStaffStudyDto) => {
      await editUserStudyModal.start(props, study);
    },
    [editUserStudyModal, props],
  );

  return (
    <ExpandedRowSections>
      <ExpandedRowSections.Column className={Style.detailsColumn}>
        <ExpandedRowSections.Section>
          <ExpandedRowHeader
            text={t('Admin.UserList.Details.Caption')}
            rightHeader={
              <div style={{ display: 'flex', gap: 8 }}>
                {props.predefinedUser && (
                  <AppButtonWithTooltip
                    variant={'icon-link'}
                    colorSchema={'decline'}
                    tooltip={t('Admin.UserList.Actions.RemoveUser')}
                    Icon={DeleteIcon}
                    onClick={onDelete}
                  />
                )}
                <AppButtonWithTooltip
                  onClick={() => editModalManager.openModal()}
                  variant={'icon-link'}
                  colorSchema={'primary'}
                  tooltip={t('Admin.UserList.Actions.EditUser')}
                  Icon={EditIcon}
                />
              </div>
            }
          />
          <PropertiesList className={Style.propertyList}>
            <PropertiesList.Property name={t('Admin.UserList.Details.Id')} value={props.id} />
            <PropertiesList.Property name={t('Admin.UserList.Details.Login')} value={props.userName} />
            <PropertiesList.Property name={t('Admin.UserList.Details.Email')} value={props.email} />
            <PropertiesList.Property name={t('Admin.UserList.Details.Timezone')} value={props.timeZone} />
            {props.roles.length > 0 && (
              <PropertiesList.Property
                name={t('Admin.UserList.Details.Roles')}
                value={
                  <BulletPoints
                    values={props.roles.map((x) =>
                      t(`Admin.UserRoles.GeneralRoles.${x}` as LocalizedResourceDictionaryKeys),
                    )}
                  />
                }
              />
            )}
          </PropertiesList>
        </ExpandedRowSections.Section>
      </ExpandedRowSections.Column>
      <ExpandedRowSections.Column colored>
        {props.studies.map((study) => (
          <StudyComponent
            key={study.id}
            study={study}
            onUnassignStudy={onUnassignStudy}
            onUserStudyEdit={onUserStudyEdit}
            isExpandDefault={false}
          />
        ))}
      </ExpandedRowSections.Column>
      <EditUserModalContent modal={editModalManager} {...props} />
      {unassignStudyConfirmationModal.modal}
      {removeUserConfirmationModal.modal}
      {editUserStudyModal.modal}
    </ExpandedRowSections>
  );
};

const StudyComponent: FC<{
  study: IStaffStudyDto;
  onUnassignStudy: (study: IStaffStudyDto) => Promise<void>;
  onUserStudyEdit: (study: IStaffStudyDto) => Promise<void>;
  isExpandDefault?: boolean;
}> = ({ study, isExpandDefault, onUnassignStudy, onUserStudyEdit }) => {
  const { t } = useTranslation('dev');
  const [isExpanded, setIsExpanded] = useState<boolean>(!!isExpandDefault);

  return (
    <ExpandedRowSections.Section key={study.id}>
      <ExpandedRowHeader
        text={
          <div className={Style.studyComponentHeader}>
            <div>
              <span>{study.studyNumber}</span>
              <span className={Style.studyTitle}>{study.studyTitle}</span>
            </div>
            <AppButton
              colorSchema={'primary'}
              variant={'icon-link'}
              onClick={() => setIsExpanded((x) => !x)}
              iconClassName={clsx(Style.expandedIcon, { [Style.chevronExpanded]: !isExpanded })}
              Icon={Chevron}
            />
          </div>
        }
        rightHeader={
          <div style={{ display: 'flex', gap: 8 }}>
            <AppButtonWithTooltip
              tooltip={t('Admin.UserList.Actions.UnassignStudy')}
              variant={'icon-link'}
              colorSchema={'decline'}
              Icon={DeleteIcon}
              onClick={() => onUnassignStudy(study)}
            />

            <AppButtonWithTooltip
              tooltip={t('Admin.UserList.Actions.EditStudyPermission')}
              variant={'icon-link'}
              colorSchema={'primary'}
              Icon={EditIcon}
              onClick={() => onUserStudyEdit(study)}
            />
          </div>
        }
      />
      <Collapse in={isExpanded} unmountOnExit>
        <PropertiesList className={Style.propertyList}>
          <PropertiesList.Property
            name={t('Admin.UserList.Details.StudyRoles')}
            value={<BulletPoints values={study.roles?.map((x) => x.name)} />}
          />
          <PropertiesList.Property
            name={t('Admin.UserList.Details.AvailableRecords')}
            value={
              <BulletPoints
                values={[...new Set(study.roles?.flatMap((x) => x.availableForms.flatMap((f) => f.type)))].sort()}
              />
            }
          />
          <PropertiesList.Property
            name={t('Admin.UserList.Details.Tags')}
            value={
              study?.tagFilters && Object.keys(study?.tagFilters).length > 0 ? (
                <div className={Style.tagColumn}>
                  {Object.entries(study?.tagFilters)
                    .sort()
                    .map(([key, values]) => (
                      <div key={key} className={Style.tag}>
                        <div className={Style.tagKey}>{`${key}:`}</div>
                        {values.map((value: string) => (
                          <Chips key={value} text={value} colorSchema={'blue'} />
                        ))}
                      </div>
                    ))}
                </div>
              ) : null
            }
          />
        </PropertiesList>
      </Collapse>
    </ExpandedRowSections.Section>
  );
};
